import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SUBP, EDITOR } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { RootsMark } from "@/components/RootsMark";
import { ReportStatus } from "@/components/waste/GapBadge";
import { ReportEditor, toPayload } from "@/components/waste/ReportEditor";
import { fmtDateTime } from "@/lib/format";
import { LogOut, Plus, MapPin, MessageSquare } from "lucide-react";
import { toast } from "sonner";

function ReportDialog({ report, me, onClose, onSaved }) {
  const [form, setForm] = useState(report);
  const locked = me.projects.find((p) => p.id === report.contract_id)?.locked;
  const editable = !locked && (!report.id || ["draft", "returned"].includes(report.status));
  const persist = async () => {
    const body = toPayload(form);
    const r = report.id ? await api.patch(`/sub/reports/${report.id}`, body)
      : await api.post("/sub/reports", { ...body, contract_id: form.contract_id, period: form.period });
    return r.data;
  };
  const save = async (submit) => {
    try {
      const saved = await persist();
      if (submit) await api.post(`/sub/reports/${saved.id}/submit`);
      toast.success(submit ? "Report submitted to Limelite" : "Draft saved"); onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async () => { try { await api.delete(`/sub/reports/${report.id}`); onSaved(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid={SUBP.dialog}>
        <DialogHeader><DialogTitle className="font-heading">{report.id ? `${report.contract_name} · ${report.period}` : "New waste report"}</DialogTitle></DialogHeader>
        {report.id && <ReportStatus status={report.status} />}
        {!report.id && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Project</Label>
              <Select value={form.contract_id} onValueChange={(v) => setForm((f) => ({ ...f, contract_id: v }))}>
                <SelectTrigger data-testid={EDITOR.project}><SelectValue placeholder="Choose project" /></SelectTrigger>
                <SelectContent>{me.projects.filter((p) => !p.locked).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Period</Label><Input type="month" value={form.period} onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))} data-testid={EDITOR.period} /></div>
          </div>
        )}
        {report.comments?.length > 0 && (
          <div className="space-y-1" data-testid={SUBP.comments}>{report.comments.map((c) => (
            <p key={c.id} className="flex items-start gap-1.5 rounded-lg bg-amber-50 p-2 text-xs text-amber-900"><MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span><b>Limelite ({c.by})</b> · {fmtDateTime(c.at)} — {c.text}</span></p>))}</div>
        )}
        {locked && <p className="rounded-lg bg-sage-surface p-2 text-xs text-forest-dark">This project's report has been issued by Limelite, so its reports are locked.</p>}
        <ReportEditor value={form} onChange={setForm} pathways={me.pathways} kinds={me.evidence_kinds} disabled={!editable} />
        {editable && (
          <DialogFooter className="gap-2">
            {report.status === "draft" && <Button variant="ghost" className="text-rose-600" onClick={remove} data-testid={SUBP.remove}>Delete draft</Button>}
            <Button variant="outline" onClick={() => save(false)} disabled={!form.contract_id || !form.period} data-testid={SUBP.save}>Save draft</Button>
            <Button className="bg-forest-mid hover:bg-forest-dark" onClick={() => save(true)} disabled={!form.contract_id || !(form.lines || []).length} data-testid={SUBP.submit}>Submit to Limelite</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SubPortalPage() {
  const { user, logout } = useAuth();
  const [me, setMe] = useState(null);
  const [reports, setReports] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([api.get("/sub/me"), api.get("/sub/reports")]);
      setMe(m.data); setReports(r.data);
    } catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const open_ = (me?.projects || []).filter((p) => !p.locked);
  const newReport = () => setOpen({ contract_id: open_[0]?.id || "", period: new Date().toISOString().slice(0, 7), lines: [], evidence: [], notes: "" });
  return (
    <div className="min-h-screen bg-canvas" data-testid={SUBP.page}>
      <header className="flex h-14 items-center justify-between bg-forest-deep px-4 text-emerald-50">
        <div className="flex items-center gap-2"><div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sage-accent"><RootsMark className="h-4 w-4 text-white" /></div>
          <div className="leading-tight"><p className="font-heading font-bold">ROOTS · Subcontractor</p><p className="-mt-0.5 text-[10px] uppercase tracking-widest text-emerald-200/70" data-testid={SUBP.company}>{me?.company?.name}</p></div></div>
        <Button size="sm" variant="ghost" className="text-emerald-50 hover:bg-white/10" onClick={logout} data-testid={SUBP.logout}><LogOut className="mr-1.5 h-4 w-4" /> Sign out</Button>
      </header>
      <main className="mx-auto max-w-3xl space-y-5 px-4 py-6">
        <div><h1 className="font-heading text-2xl font-bold text-forest-dark">Hello {user?.name}</h1>
          <p className="text-sm text-muted-foreground">Report how waste is collected, treated and disposed of on your assigned Limelite projects.</p></div>
        <section><h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">My projects</h2>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">{(me?.projects || []).map((p) => (
            <Card key={p.id} className="border-sage-light p-3" data-testid={SUBP.project(p.id)}>
              <p className="text-sm font-semibold text-forest-dark">{p.name}</p>
              <p className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {p.sites.join(", ")} · {p.status === "completed" ? "Completed" : "Ongoing"}{p.locked ? " · report issued (locked)" : ""}</p>
            </Card>))}
            {me && me.projects.length === 0 && <p className="text-sm text-muted-foreground">You are not assigned to any project yet.</p>}
          </div></section>
        <section>
          <div className="mb-2 flex items-center justify-between"><h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">My reports</h2>
            <Button size="sm" className="bg-forest-mid hover:bg-forest-dark" onClick={newReport} disabled={!open_.length} data-testid={SUBP.newReport}><Plus className="mr-1 h-4 w-4" /> New report</Button></div>
          <div className="divide-y rounded-xl border border-sage-light bg-white">
            {reports.map((r) => (
              <button key={r.id} onClick={() => setOpen(r)} className="flex w-full items-center gap-2 px-3 py-3 text-left hover:bg-sage-surface" data-testid={SUBP.report(r.id)}>
                <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium text-forest-dark">{r.contract_name}</span>
                  <span className="block text-xs text-muted-foreground">{r.period} · {(r.lines || []).length} line(s){r.entered_by_limelite ? " · entered by Limelite" : ""}</span></span>
                {r.status === "returned" && <MessageSquare className="h-4 w-4 text-amber-600" />}
                <ReportStatus status={r.status} />
              </button>))}
            {reports.length === 0 && <p className="p-4 text-sm text-muted-foreground">No reports yet.</p>}
          </div>
        </section>
      </main>
      {open && me && <ReportDialog report={open} me={me} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </div>
  );
}
